import React, { useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Filter, X, Search, ChevronDown, ChevronUp, MapPin, CalendarDays } from 'lucide-react';

/**
 * Generic helper to compute event date range presets.
 * Returns {from: 'YYYY-MM-DD', to: 'YYYY-MM-DD'} (or empty strings).
 */
export const eventDatePresets = {
  hari_ini: () => {
    const today = new Date().toISOString().slice(0, 10);
    return { from: today, to: today };
  },
  minggu_ini: () => {
    const now = new Date();
    const day = now.getDay() || 7; // Mon=1...Sun=7
    const mon = new Date(now);
    mon.setDate(now.getDate() - (day - 1));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { from: mon.toISOString().slice(0, 10), to: sun.toISOString().slice(0, 10) };
  },
  bulan_ini: () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    return { from: first, to: last };
  },
  upcoming: () => {
    const today = new Date().toISOString().slice(0, 10);
    return { from: today, to: '' };
  },
  past: () => {
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    return { from: '', to: yesterday };
  },
};

/**
 * AdvancedFilterBar
 *
 * Props:
 *   value              — { search, status, venue, event_date_from, event_date_to }
 *   onChange(patch)    — partial update handler ({...prev, ...patch})
 *   onClear()          — reset everything to empty
 *   searchPlaceholder  — placeholder for the search box
 *   statusOptions      — [{value, label}, ...]
 *   testidPrefix       — used for data-testid (e.g. 'invoices-filter')
 */
export const AdvancedFilterBar = ({
  value = {},
  onChange,
  onClear,
  searchPlaceholder = 'Cari…',
  statusOptions = [],
  testidPrefix = 'filter',
}) => {
  const [open, setOpen] = useState(false);

  const v = {
    search: '',
    status: '',
    venue: '',
    event_date_from: '',
    event_date_to: '',
    ...value,
  };

  const activeAdvancedCount = [v.venue, v.event_date_from, v.event_date_to].filter(Boolean).length
    + (v.status ? 1 : 0);

  const applyPreset = (presetKey) => {
    const range = eventDatePresets[presetKey]();
    onChange({ event_date_from: range.from, event_date_to: range.to });
    if (!open) setOpen(true);
  };

  const isPresetActive = (presetKey) => {
    const r = eventDatePresets[presetKey]();
    return v.event_date_from === r.from && v.event_date_to === r.to;
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-bar`}>
      {/* Main search row + filter toggle */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={v.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder={searchPlaceholder}
            className="pl-9 h-11 md:h-10 text-base md:text-sm"
            data-testid={`${testidPrefix}-search`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={open ? 'default' : 'secondary'}
            size="sm"
            className="h-11 md:h-10 px-3 gap-1.5"
            onClick={() => setOpen(o => !o)}
            data-testid={`${testidPrefix}-toggle`}
          >
            <Filter className="h-4 w-4" />
            <span>Filter</span>
            {activeAdvancedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1">
                {activeAdvancedCount}
              </span>
            )}
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
          {(v.search || activeAdvancedCount > 0) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-11 md:h-10 px-2 gap-1.5 text-muted-foreground"
              onClick={() => { onClear && onClear(); }}
              data-testid={`${testidPrefix}-clear`}
            >
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">Reset</span>
            </Button>
          )}
        </div>
      </div>

      {/* Collapsible advanced filters */}
      {open && (
        <div className="rounded-lg border border-border bg-card/40 p-3 space-y-3" data-testid={`${testidPrefix}-panel`}>
          {/* Quick presets */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground mr-1 flex items-center gap-1">
              <CalendarDays className="h-3 w-3" /> Tanggal Event:
            </span>
            <PresetChip
              label="Hari ini"
              active={isPresetActive('hari_ini')}
              onClick={() => applyPreset('hari_ini')}
              testid={`${testidPrefix}-preset-today`}
            />
            <PresetChip
              label="Minggu ini"
              active={isPresetActive('minggu_ini')}
              onClick={() => applyPreset('minggu_ini')}
              testid={`${testidPrefix}-preset-week`}
            />
            <PresetChip
              label="Bulan ini"
              active={isPresetActive('bulan_ini')}
              onClick={() => applyPreset('bulan_ini')}
              testid={`${testidPrefix}-preset-month`}
            />
            <PresetChip
              label="Upcoming"
              active={isPresetActive('upcoming')}
              onClick={() => applyPreset('upcoming')}
              testid={`${testidPrefix}-preset-upcoming`}
            />
            <PresetChip
              label="Past"
              active={isPresetActive('past')}
              onClick={() => applyPreset('past')}
              testid={`${testidPrefix}-preset-past`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="text-[11px]">Event dari</Label>
              <Input
                type="date"
                value={v.event_date_from}
                onChange={(e) => onChange({ event_date_from: e.target.value })}
                className="h-11 md:h-9 text-base md:text-sm date-input-mobile"
                data-testid={`${testidPrefix}-event-from`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px]">Event sampai</Label>
              <Input
                type="date"
                value={v.event_date_to}
                onChange={(e) => onChange({ event_date_to: e.target.value })}
                className="h-11 md:h-9 text-base md:text-sm date-input-mobile"
                data-testid={`${testidPrefix}-event-to`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] flex items-center gap-1"><MapPin className="h-3 w-3" />Venue</Label>
              <Input
                value={v.venue}
                onChange={(e) => onChange({ venue: e.target.value })}
                placeholder="Cari venue…"
                className="h-11 md:h-9 text-base md:text-sm"
                data-testid={`${testidPrefix}-venue`}
              />
            </div>
            {statusOptions.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-[11px]">Status</Label>
                <Select
                  value={v.status || '__all__'}
                  onValueChange={(val) => onChange({ status: val === '__all__' ? '' : val })}
                >
                  <SelectTrigger className="h-11 md:h-9 text-base md:text-sm" data-testid={`${testidPrefix}-status`}>
                    <SelectValue placeholder="Semua status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Semua status</SelectItem>
                    {statusOptions.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PresetChip = ({ label, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={[
      'inline-flex items-center rounded-full border text-xs px-2.5 py-1 transition-colors',
      active
        ? 'bg-primary text-primary-foreground border-primary'
        : 'bg-card/60 text-foreground border-border hover:border-primary/40',
    ].join(' ')}
  >
    {label}
  </button>
);
